import { useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectProjectUsers,
  selectProjectModalOpen,
  selectProjectError,
  selectUserRole,
  selectUserId,
} from '@/store/selectors';
import {
  updateProjectUsers,
  updateInvitedUsers,
  updateProjectId,
  updateProjectModal,
  updateUserRole,
  setTasks,
  setMessages,
  setSelectedItem,
  setProjectMetadata,
} from '../../app/features/project/projectSlice';
import { getProjectById, getProjectFilesNormalized } from '@/api/project';
import { setItemsForParent, selectItemsForParent, selectCacheAge, CACHE_MAX_AGE_MS } from '@/store/itemsSlice';
import { getProjectTasks } from '@/api/task';
import { getMessagesByProject } from '@/api/message';
import { usersByProject } from '../../apiServices/userApi';
import { useNotification } from '@/hooks/useNotification';
import { useSession } from '@/hooks/useSession';
import { ProjectFilesView } from './ProjectFilesView';
import { ProjectMainView } from './ProjectMainView';
import { ProjectFooter } from './ProjectFooter';
import { MainViewLoading } from './components/MainViewLoading';
import { Modal } from '@/components/ui/Modal';
import ProjectParticipiantsModal from '../../components/modals/projectParticipiantsModal/ProjectParticipiantsModal';

export default function ProjectPage() {
  const { id: projectId, viewType } = useParams<{ id: string; viewType?: string }>();
  const dispatch = useAppDispatch();
  const { notify } = useNotification();
  const { updateCurrentItem } = useSession();
  const cancelledRef = useRef(false);

  const currentUserInfo = useAppSelector((state) => state.user.oldUserDetails);
  const users = useAppSelector(selectProjectUsers) as unknown[] | null;
  const modalOpen = useAppSelector(selectProjectModalOpen);
  const error = useAppSelector(selectProjectError);
  const userRole = useAppSelector(selectUserRole);
  const uid = useAppSelector(selectUserId);
  const currentUserGroup = useAppSelector((state) => state.workspace.currentUserGroup) as string | null;

  // ── Items from global cache ───────────────────────────────────
  const cachedItems = useAppSelector(selectItemsForParent(projectId));
  const cacheAge = useAppSelector(selectCacheAge(projectId));
  const isCacheStale = cacheAge !== undefined && cacheAge > CACHE_MAX_AGE_MS;

  const fetchProjectData = useCallback(async () => {
    if (!projectId) return;
    cancelledRef.current = false;

    // Fetch metadata, users, tasks, messages in parallel (always needed)
    getProjectById(projectId)
      .then((res) => {
        if (cancelledRef.current) return;
        dispatch(setProjectMetadata(res.response.Item));
        // Set breadcrumb item
        const item = res.response.Item as Record<string, { S?: string }> | undefined;
        if (item) {
          updateCurrentItem({
            id: item.id?.S || item.id,
            title: item.title?.S || item.title,
            itemtype: 'project',
            projectId: item.id?.S || item.id,
            breadcrumb: (item as Record<string, unknown>).breadcrumb,
          });
        }
      })
      .catch(() => {});

    // Items: fetch into global cache if stale or missing
    if (!cachedItems || cachedItems.length === 0 || isCacheStale) {
      getProjectFilesNormalized(projectId)
        .then((normalized) => {
          if (cancelledRef.current) return;
          dispatch(setItemsForParent({ parentId: projectId!, items: normalized }));
        })
        .catch(() => {});
    }

    usersByProject(projectId)
      .then((res: { response: { Items: unknown[]; invitedUsers?: unknown[] } }) => {
        if (cancelledRef.current) return;
        dispatch(updateProjectUsers(res.response.Items));
        dispatch(updateInvitedUsers(res.response?.invitedUsers));
        // Find current user's role
        const userList = res.response.Items as Array<{ uid: string; role: string }>;
        const myRole = userList.find((u) => u.uid === uid)?.role;
        dispatch(updateUserRole(myRole ?? null));
      })
      .catch(() => {});

    getProjectTasks(projectId)
      .then((res) => {
        if (cancelledRef.current) return;
        dispatch(setTasks(res.response.Items));
      })
      .catch(() => {});

    getMessagesByProject(projectId)
      .then((res) => {
        if (cancelledRef.current) return;
        dispatch(setMessages(res.response.Items));
      })
      .catch(() => {});
  }, [projectId, uid, dispatch, cachedItems, isCacheStale]);

  // Load project data on mount and when project ID changes
  useEffect(() => {
    dispatch(updateProjectId(projectId));
    fetchProjectData();

    return () => {
      cancelledRef.current = true;
      dispatch(updateProjectUsers(null));
      dispatch(updateInvitedUsers([]));
      dispatch(setMessages(null));
      dispatch(setTasks(null));
      dispatch(updateUserRole(null));
      dispatch(setSelectedItem(null));
      dispatch(updateProjectId(null));
    };
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  function toggleModal() {
    if (error) {
      notify({ id: String(Date.now()), type: 'info', message: 'Could not find project users list' });
    } else {
      dispatch(updateProjectModal(!modalOpen));
    }
  }

  // Determine which view to show
  const isFilesView = !!viewType;
  const isDataReady = !!(users as unknown[])?.length && cachedItems && uid;

  let mainView;
  if (isFilesView) {
    mainView = (
      <ProjectFilesView fileListView={viewType} />
    );
  } else if (!isDataReady) {
    mainView = <MainViewLoading previewListItemsAmount={13} />;
  } else {
    mainView = (
      <ProjectMainView currentUserGroup={currentUserGroup} />
    );
  }

  return (
    <div className="flex min-h-[calc(100dvh-8.5rem)] w-full flex-col">
      <div className="flex-1 pb-14">
        {currentUserInfo && mainView}
      </div>
      <ProjectFooter
        baseUrl={`/in/project/${projectId}`}
        active={viewType}
      />
      <Modal open={modalOpen} onClose={toggleModal} title="Project participants" size="md">
        <ProjectParticipiantsModal
          closeModal={toggleModal}
          projectId={projectId!}
          userRole={userRole || ''}
        />
      </Modal>
    </div>
  );
}
